import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

export const useGoogleCalendar = () => {
  const { isAuthenticated, token, loading: authLoading, getToken } = useAuth();
  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    isSupporter: false,
    configured: true,
    loading: true,
    error: null,
    calendarsConfigured: 0,
    calendarsEnabled: 0,
    accountCount: 0
  });

  const fetchCalendarStatus = useCallback(async () => {
    try {
      setCalendarStatus(prev => ({ ...prev, loading: true, error: null }));

      // Always attempt to use a fresh token (prevents intermittent 401s)
      const authHeaderToken = isAuthenticated ? (await getToken()) || token : null;
      let headers = { 'Content-Type': 'application/json' };
      if (authHeaderToken) headers['Authorization'] = `Bearer ${authHeaderToken}`;

      let response = await fetch('/api/google/status', { method: 'GET', headers });

      const data = await response.json();

      // If token is expired, refresh token and retry once
      if (!response.ok) {
        if (response.status === 401 && isAuthenticated) {
          const refreshed = await getToken();
          headers = { 'Content-Type': 'application/json' };
          if (refreshed) headers['Authorization'] = `Bearer ${refreshed}`;
          response = await fetch('/api/google/status', { method: 'GET', headers });
          const retryData = await response.json();
          if (!response.ok) {
            throw new Error(retryData.error || 'Failed to fetch calendar status');
          }
          // replace data with retryData on success
          Object.assign(data, retryData);
        } else {
          throw new Error(data.error || 'Failed to fetch calendar status');
        }
      }

      const newStatus = {
        connected: data.connected,
        isSupporter: data.isSupporter,
        configured: data.configured !== false, // Default to true if not specified
        loading: false,
        error: null,
        calendarsConfigured: data.calendarsConfigured || 0,
        calendarsEnabled: data.calendarsEnabled || 0,
        accountCount: data.accountCount || (data.connected ? 1 : 0),
        tokenExpiry: data.tokenExpiry,
        lastUpdated: data.lastUpdated
      };
      
      setCalendarStatus(newStatus);
      return newStatus; // Return the new status

    } catch (error) {
      console.error('Error fetching calendar status:', error);
      const errorStatus = {
        connected: false,
        isSupporter: false,
        configured: true,
        loading: false,
        error: error.message,
        calendarsConfigured: 0,
        calendarsEnabled: 0,
        accountCount: 0
      };
      setCalendarStatus(prev => ({ ...prev, ...errorStatus }));
      return errorStatus; // Return the error status
    }
  }, [isAuthenticated, token]);

  const startGoogleAuth = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/google/auth/start', {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start Google authorization');
      }

      // Open Google OAuth in a popup
      const popup = window.open(
        data.authUrl,
        'google-oauth',
        'width=600,height=600,scrollbars=yes,resizable=yes,noopener=no,noreferrer=no'
      );

      if (!popup) {
        throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      }

      return new Promise((resolve, reject) => {
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            // Check if auth was successful by refetching status
            fetchCalendarStatus();
            resolve(true);
          }
        }, 1000);

        // Listen for messages from the popup
        const messageListener = async (event) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
            clearInterval(checkClosed);
            if (popup && !popup.closed) {
              popup.close();
            }
            window.removeEventListener('message', messageListener);
            
            try {
              // Exchange the authorization code for tokens
              await exchangeCodeForTokens(event.data.code);
              resolve(true);
            } catch (error) {
              reject(error);
            }
          } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
            clearInterval(checkClosed);
            popup.close();
            window.removeEventListener('message', messageListener);
            reject(new Error(event.data.error || 'Google authorization failed'));
          }
        };

        window.addEventListener('message', messageListener);

        // Cleanup after 5 minutes
        setTimeout(() => {
          clearInterval(checkClosed);
          if (!popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', messageListener);
          reject(new Error('Authorization timeout'));
        }, 5 * 60 * 1000);
      });

    } catch (error) {
      console.error('Error starting Google auth:', error);
      throw error;
    }
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch('/api/google/auth/exchange', {
        method: 'POST',
        headers,
        body: JSON.stringify({ code })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to exchange authorization code');
      }

      // Refresh calendar status
      await fetchCalendarStatus();

      return data;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      throw error;
    }
  };

  const disconnectCalendar = async () => {
    // Helper function to make the API call
    const makeDisconnectCall = async (authToken) => {
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      };

      const response = await fetch('/api/google/disconnect', {
        method: 'POST',
        headers
      });

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.error || 'Failed to disconnect Google Calendar');
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    };

    try {
      // First attempt with current token
      const data = await makeDisconnectCall(token);
      
      // Refresh calendar status on success
      await fetchCalendarStatus();
      return data;
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      
      // If we get a 401 error, try refreshing the token and retry once
      if (error.status === 401 && isAuthenticated) {
        try {
          console.log('Auth token expired, refreshing and retrying disconnect...');
          const freshToken = await getToken();
          
          if (freshToken) {
            const data = await makeDisconnectCall(freshToken);
            await fetchCalendarStatus();
            return data;
          }
        } catch (retryError) {
          console.error('Retry after token refresh also failed:', retryError);
          // Fall through to original error handling
        }
      }
      
      throw error;
    }
  };

  // Only fetch status when authenticated and not already loaded
  useEffect(() => {
    if (!authLoading && isAuthenticated && token && calendarStatus.loading) {
      fetchCalendarStatus();
    }
  }, [isAuthenticated, token, authLoading, calendarStatus.loading, fetchCalendarStatus]);

  return {
    ...calendarStatus,
    startGoogleAuth,
    exchangeCodeForTokens,
    disconnectCalendar,
    refreshStatus: fetchCalendarStatus
  };
};